import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, UserPlus } from "lucide-react";

const ROLES = { employee: "Employee", store_manager: "Store manager", accountant: "Accountant", owner: "Owner" };

function NewStaff({ stores, onDone }) {
  const blank = { name: "", email: "", role: "employee", password: "", assigned_stores: [] };
  const [f, setF] = useState(blank);
  const toggle = (id) => setF({ ...f, assigned_stores: f.assigned_stores.includes(id) ? f.assigned_stores.filter((s) => s !== id) : [...f.assigned_stores, id] });
  const save = async () => {
    try { await api.post("/auth/users", f); toast.success("Account created"); setF(blank); onDone(); }
    catch (e) { toast.error(apiError(e, "Could not create the account")); }
  };
  return (
    <div className="rounded-xl border border-navy/10 bg-white p-4 space-y-3" data-testid="staff-new-form">
      <p className="font-semibold text-navy flex items-center gap-2"><UserPlus className="w-4 h-4" /> New staff account</p>
      <Input placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="staff-name-input" />
      <Input placeholder="Email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="staff-email-input" />
      <select className="w-full rounded-md border border-navy/20 p-2 text-sm" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="staff-role-select">
        {Object.entries(ROLES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      <div className="flex gap-4 text-sm">{stores.map((s) => (
        <label key={s.id} className="flex items-center gap-2"><input type="checkbox" checked={f.assigned_stores.includes(s.id)} onChange={() => toggle(s.id)} data-testid={`staff-store-${s.id}`} />{s.name}</label>
      ))}</div>
      <Input placeholder="Starting password (10+ characters)" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="staff-password-input" />
      <Button className="w-full" onClick={save} data-testid="staff-create-btn">Create account</Button>
    </div>
  );
}

function StaffRow({ u, stores, onDone }) {
  const [pw, setPw] = useState("");
  const upd = async (body, msg) => {
    try { await api.put(`/auth/users/${u.id}`, body); toast.success(msg); setPw(""); onDone(); }
    catch (e) { toast.error(apiError(e, "Could not update the account")); }
  };
  const names = stores.filter((s) => (u.assigned_stores || u.store_ids || []).includes(s.id)).map((s) => s.name).join(", ") || "No stores";
  return (
    <div className="rounded-xl border border-navy/10 bg-white p-4 space-y-2" data-testid={`staff-row-${u.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div><p className="font-semibold text-navy">{u.name}</p><p className="text-xs text-navy/60 break-all">{u.email}</p>
          <p className="text-xs text-navy/60">{ROLES[u.role] || u.role} · {names}</p></div>
        <span className={`text-xs px-2 py-1 rounded-full ${u.is_active ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`} data-testid={`staff-status-${u.id}`}>{u.is_active ? "Active" : "Disabled"}</span>
      </div>
      <div className="flex gap-2">
        <Input type="password" placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid={`staff-newpw-${u.id}`} />
        <Button variant="outline" onClick={() => upd({ password: pw }, "Password changed")} disabled={!pw} data-testid={`staff-setpw-${u.id}`}>Set</Button>
      </div>
      <Button variant="outline" className="w-full" onClick={() => upd({ is_active: !u.is_active }, u.is_active ? "Account disabled" : "Account enabled")} data-testid={`staff-toggle-${u.id}`}>
        {u.is_active ? "Disable account" : "Enable account"}
      </Button>
    </div>
  );
}

export default function StaffPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [stores, setStores] = useState([]);
  const load = async () => {
    try {
      const [u, s] = await Promise.all([api.get("/auth/users"), api.get("/stores")]);
      setUsers(u.data); setStores(s.data);
    } catch (e) { toast.error(apiError(e, "Could not load staff")); }
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-4" data-testid="staff-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/dashboard")} data-testid="staff-back-btn"><ArrowLeft className="w-5 h-5 text-navy" /></button>
        <h1 className="text-xl font-heading font-bold text-navy">Staff accounts</h1>
      </div>
      <NewStaff stores={stores} onDone={load} />
      {users.map((u) => <StaffRow key={u.id} u={u} stores={stores} onDone={load} />)}
    </div>
  );
}
